import {CustomError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {ActionBase, ActionWithPayload} from '../../../helpers/reduxHelpers';
import {AnswerFolderModel} from '../../../models/api/answerFolderModel';
import {ApiAnswerFolders} from '../../../models/api/answerFoldersModel';
import {fetchApiAsync} from '../../../services/http/http';
import {HttpResponseError} from '../../../services/http/httpError';
import {ThunkAsyncAction} from '../../appThunk';
import {getCompany} from '../company/companySelectors';
import {DataUpdatedAction, updateDataAction} from '../dataGroupsActions';

/*
 * Action types.
 */

export enum AnswerFolderActionsEnum {
  ANSWER_FOLDER_DELETED = '@@data/answerFolders/ANSWER_FOLDER_DELETED',
  ANSWER_FOLDERS_DELETED = '@@data/answerFolders/ANSWER_FOLDERS_DELETED',
  FETCH_ANSWER_FOLDERS = '@@data/answerFolders/FETCH_ANSWER_FOLDERS',
  FETCH_ANSWER_FOLDERS_COMPLETED = '@@data/answerFolders/FETCH_ANSWER_FOLDERS_COMPLETED',
  FETCH_ANSWER_FOLDERS_FAILED = '@@data/answerFolders/FETCH_ANSWER_FOLDERS_FAILED',
  ANSWER_FOLDERS_MARK_AS_STALE = '@@data/answerFolders/ANSWER_FOLDERS_MARK_AS_STALE',
}

/*
 * Types.
 */

export interface AnswerFolderUpdatedAction extends DataUpdatedAction {}

export interface AnswerFolderDeletedAction extends ActionWithPayload<number> {
  type: AnswerFolderActionsEnum.ANSWER_FOLDER_DELETED;
}

export interface AnswerFoldersDeletedAction extends ActionWithPayload<ReadonlyArray<number>> {
  type: AnswerFolderActionsEnum.ANSWER_FOLDERS_DELETED;
}

export interface FetchAnswerFoldersAction extends ActionBase {
  type: AnswerFolderActionsEnum.FETCH_ANSWER_FOLDERS;
}

export interface FetchAnswerFoldersCompletedAction
  extends ActionWithPayload<ReadonlyArray<AnswerFolderModel>> {
  type: AnswerFolderActionsEnum.FETCH_ANSWER_FOLDERS_COMPLETED;
}

export interface FetchAnswerFoldersFailedAction extends ActionWithPayload<string> {
  type: AnswerFolderActionsEnum.FETCH_ANSWER_FOLDERS_FAILED;
}

export interface AnswerFoldersMarkAsStaleAction extends ActionBase {
  type: AnswerFolderActionsEnum.ANSWER_FOLDERS_MARK_AS_STALE;
}

export class AnswerFolderNameAlreadyExistsError extends CustomError {}

/*
 * Action creators.
 */

export function updateAnswerFolderAction(answerFolder: AnswerFolderModel): AnswerFolderUpdatedAction {
  return updateDataAction({answerFolders: [answerFolder]});
}

export function deleteAnswerFolderAction(answerFolderId: number): AnswerFolderDeletedAction {
  return {
    payload: answerFolderId,
    type: AnswerFolderActionsEnum.ANSWER_FOLDER_DELETED,
  };
}

export function fetchAllAnswerFoldersAction(): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);

    dispatch(fetchAnswerFoldersAction());

    try {
      const url = `${company.url}/canned_answer_folders`;
      const answerFolders = await fetchApiAsync(ApiAnswerFolders, url);
      dispatch(fetchAnswerFoldersCompletedAction(answerFolders.cannedAnswerFolders));
    } catch (error) {
      const errorMessage = error instanceof HttpResponseError ? error.reason : undefined;
      dispatch(fetchAnswerFoldersFailedAction(errorMessage));
    }
  };
}

export function markAnswerFoldersAsStaleAction(): AnswerFoldersMarkAsStaleAction {
  return {
    type: AnswerFolderActionsEnum.ANSWER_FOLDERS_MARK_AS_STALE,
  };
}

function fetchAnswerFoldersAction(): FetchAnswerFoldersAction {
  return {
    type: AnswerFolderActionsEnum.FETCH_ANSWER_FOLDERS,
  };
}

function fetchAnswerFoldersCompletedAction(
  answerFolders: ReadonlyArray<AnswerFolderModel>,
): FetchAnswerFoldersCompletedAction {
  return {
    type: AnswerFolderActionsEnum.FETCH_ANSWER_FOLDERS_COMPLETED,
    payload: answerFolders,
  };
}

function fetchAnswerFoldersFailedAction(error?: string): FetchAnswerFoldersFailedAction {
  return {
    type: AnswerFolderActionsEnum.FETCH_ANSWER_FOLDERS_FAILED,
    payload: error || 'Failed to fetch answer folders.',
  };
}

/*
 * Union type.
 */

export type AnswerFolderAction =
  | AnswerFolderUpdatedAction
  | AnswerFolderDeletedAction
  | AnswerFoldersDeletedAction
  | FetchAnswerFoldersAction
  | FetchAnswerFoldersCompletedAction
  | FetchAnswerFoldersFailedAction
  | AnswerFoldersMarkAsStaleAction;
